import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import authService from '../services/authService';
import storageUtils from '../utils/storage';

const AuthContext = createContext();

const isTokenExpired = (token) => {
  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(storageUtils.getAuthToken());
  const [refreshToken, setRefreshToken] = useState(storageUtils.getItem('refresh_token'));
  const [user, setUser] = useState(storageUtils.getUserData());
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    // Server-side blacklist happens inside authService.logout(); awaiting
    // lets us send the refresh token before clearing local state.
    await authService.logout();
    setToken(null);
    setRefreshToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    if (isTokenExpired(token)) {
      logout();
      setLoading(false);
      return;
    }

    if (!user) {
      try {
        const decoded = jwtDecode(token);
        setUser(decoded);
        storageUtils.setUserData(decoded);
      } catch (err) {
        console.error('Failed to decode token:', err);
        logout();
      }
    }

    setLoading(false);
  }, [token, user, logout]);

  useEffect(() => {
    if (!refreshToken) {
      return undefined;
    }

    const interval = setInterval(async () => {
      try {
        const data = await authService.refreshAccessToken();
        if (data?.access) {
          setToken(data.access);
          storageUtils.setAuthToken(data.access);
        }
        // Refresh tokens rotate — save the new one so the next refresh works.
        if (data?.refresh) {
          setRefreshToken(data.refresh);
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        logout();
      }
    }, 1000 * 60 * 10);

    return () => clearInterval(interval);
  }, [refreshToken, logout]);

  const login = async (username, password) => {
    const data = await authService.login({ username, password });

    if (data?.requires_2fa) {
      return {
        success: false,
        requires_2fa: true,
        otp_session_id: data.otp_session_id,
        masked_phone: data.masked_phone,
        masked_email: data.masked_email,
        message: data.message,
      };
    }

    if (data?.success && data.access) {
      setToken(data.access);
      setRefreshToken(data.refresh);
      setUser(data.user);
      storageUtils.setAuthToken(data.access);
      storageUtils.setItem('refresh_token', data.refresh);
      storageUtils.setUserData(data.user);
      return { success: true, role: data.user?.role };
    }

    return { success: false, message: data?.message };
  };

  const verifyTwoFactor = async (payload) => {
    const data = await authService.verifyTwoFactor(payload);
    if (data?.success && data.access) {
      setToken(data.access);
      setRefreshToken(data.refresh);
      setUser(data.user);
      storageUtils.setAuthToken(data.access);
      storageUtils.setItem('refresh_token', data.refresh);
      storageUtils.setUserData(data.user);
    }
    return data;
  };

  const resendTwoFactor = async (payload) => {
    return authService.resendTwoFactor(payload);
  };

  const changePassword = async ({ currentPassword, newPassword, confirmPassword }) => {
    return authService.changePassword({ currentPassword, newPassword, confirmPassword });
  };

  const requestEmailChange = async ({ currentPassword, newEmail }) => {
    return authService.requestEmailChange({ currentPassword, newEmail });
  };

  const confirmEmailChange = async ({ token }) => {
    return authService.confirmEmailChange({ token });
  };

  const register = async (userData) => {
    const result = await authService.register(userData);

    if (!result.success) {
      console.error('[REGISTER FAILED]', result.message);
    }

    return result;
  };

  const forgotPassword = async (username) => {
    return authService.forgotPassword(username);
  };

  const resetPassword = async (args) => {
    return authService.resetPassword(args);
  };

  return (
    <AuthContext.Provider value={{ user, token, refreshToken, loading, login, verifyTwoFactor, resendTwoFactor, logout, requestEmailChange, confirmEmailChange, changePassword, register, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
